<template>
  <div class="integrations-grid mt-8" data-testid="integrations-grid">
    <h2 class="text-xl font-semibold mb-3">Workflows</h2>
    <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
      Set up automatic or 1-click integrations with Zight. Automatically create content from step-by-step guides or update your customer tickets when receiving
      videos.
    </p>
    <p v-if="!hasBusinessPlan" class="zt-info-box mb-6" data-testid="plan-notice">
      Please note: 1-click integrations are available on our Enterprise plan. Contact your customer success manager to help you get set up.
    </p>
    <template v-if="hasIntegrations">
      <div data-testid="integrations-grid-container">
        <ul class="cards-grid">
          <IntegrationCard
            v-for="integration in sortedIntegrations"
            :key="integration.name"
            :integration="integration"
            @toggle="handleToggle"
            @request-enable="handleRequestEnable"
          />
        </ul>
      </div>
    </template>
    <template v-else-if="loading">
      <div class="flex items-center justify-center py-8" data-testid="loading-state">
        <div class="text-zinc-500">Loading integrations...</div>
      </div>
    </template>
    <template v-else-if="error">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="error-state">
        <p class="text-red-700">{{ error }}</p>
      </div>
    </template>
    <template v-else>
      <p data-testid="no-integrations-message">No integrations available. Contact your account admin to enable them.</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, computed } from 'vue';

import { useIntegrationStore } from '../../store/useIntegrationStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useIntegrationActions } from '../../composables/useIntegrationActions';
import { storeToRefs } from 'pinia';
import { paragon } from '@useparagon/connect';
import IntegrationCard from '../cards/IntegrationCard.vue';

const integrationStore = useIntegrationStore();
const organizationStore = useOrganizationStore();

// Use the composable for integration actions
const { handleRequestEnable } = useIntegrationActions();

const { integrations, loading, error, token, projectId, sortedIntegrations } = storeToRefs(integrationStore);
const { hasBusinessPlan } = storeToRefs(organizationStore);

const hasIntegrations = computed(() => integrations.value.length > 0);

onMounted(() => integrationStore.fetchIntegrations());

watch(token, newToken => paragon.authenticate(projectId.value ?? '', newToken ?? ''));

// Handle integration success callback for the local paragon.connect calls
const handleSuccess = () => {
  integrationStore.fetchIntegrations(true);
};

// Local toggle function that handles paragon authentication flow
const handleToggle = async (name: string) => {
  await paragon.authenticate(projectId.value ?? '', token.value ?? '');
  paragon.connect(name.toLowerCase(), { onInstall: handleSuccess, onUninstall: handleSuccess });
};
</script>

<style scoped>
.integrations-grid {
  @apply py-8;
}

.zt-info-box {
  @apply bg-blue-50 border border-blue-200 p-4 rounded mt-4 mb-8 text-zinc-700;
  @apply dark:bg-blue-900 dark:border-blue-500 dark:text-zinc-200;
}

.cards-grid {
  @apply grid gap-6 mt-4;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}
</style>
